import { useMutation } from '@apollo/client/react';
import Compressor from 'compressorjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import events from '@guuru/events-web';
import { i18n } from '@guuru/translation-web';
import uploadGCSSignedUrl from '../uploadGCSSignedUrl';
import UPLOAD_CHAT_ATTACHMENT from './mutations/UploadChatAttachment';

interface UseAttachmentSubmitOpts {
  chatId: string;
  onStart?: () => void;
  onProgress?: (message: string) => void;
  onUpload?: (
    readUrl: string,
    filename: string,
    mimetype: string,
  ) => void | Promise<void>;
  onError?: (message: string) => void;
  onFinish?: () => void;
  allowedTypes?: RegExp[];
}

interface AttachmentInputEvent {
  target: {
    files: Array<File> | FileList | null;
  };
}

const useAttachmentSubmit = function ({
  chatId,
  onStart = () => {},
  onProgress = () => {},
  onUpload = () => {},
  onError = () => {},
  onFinish = () => {},
  allowedTypes = [
    /application\/*/,
    /image\/*/,
    /text\/*/,
    /video\/mp4/,
    /audio\/mpeg/,
  ],
}: UseAttachmentSubmitOpts) {
  const [uploadChatAttachment] = useMutation(UPLOAD_CHAT_ATTACHMENT);
  const { t } = useTranslation(undefined, { i18n });
  return useCallback(
    async ({ target: { files } }: AttachmentInputEvent) => {
      const [file] = files ?? [];
      if (!file) return null;

      let blob: File | Blob = file;
      if (!allowedTypes.some((type) => type.test(file.type))) {
        onError('Oops, you can only upload images, videos and documents.');
        return null;
      }

      onStart();
      onProgress(t('chat:::uploadingFile'));

      try {
        if (/image\/*/.test(file.type)) {
          blob = await new Promise<File | Blob>((resolve, reject) => {
            Compressor.create(file, {
              quality: 0.8,
              mimeType: file.type,
              maxWidth: 1200,
              maxHeight: 1200,
              success: resolve,
              error: reject,
            });
          });
        }

        const filename = file.name;
        const mimetype = file.type;

        const { data } = await uploadChatAttachment({
          variables: {
            id: chatId,
            input: {
              filename,
              mimetype,
            },
          },
        });

        if (!data?.uploadChatAttachment) {
          onError('Oops, something went wrong uploading your file. Try again.');
          return null;
        }
        const { writeUrl, readUrl, maxBytes, acl } = data.uploadChatAttachment;

        if (blob.size > maxBytes) {
          onError(t('chat:::uploadFileMaxSize'));
          return null;
        }

        events.attachmentSubmit(file.type);

        await uploadGCSSignedUrl({
          contentType: mimetype,
          writeUrl,
          blob,
          maxBytes,
          acl,
        });

        await onUpload(readUrl, filename, mimetype);
        onFinish();
        return readUrl;
      } catch (err) {
        onError(err instanceof Error ? err.message : String(err));
        return null;
      }
    },
    [
      chatId,
      uploadChatAttachment,
      allowedTypes,
      onStart,
      onError,
      onUpload,
      onFinish,
      onProgress,
      t,
    ],
  );
};

export default useAttachmentSubmit;
